
import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSupabaseAuth } from '@/hooks/auth';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Bug, RefreshCw, CheckCircle2, Clock } from 'lucide-react';
import AuthStatus from '@/components/debug/AuthStatus';
import DataHealthCheck from '@/components/debug/DataHealthCheck';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

// Import components that should load immediately
import HeroSection from '@/components/home/HeroSection';
import FooterSection from '@/components/home/FooterSection';

// Lazy load below-the-fold sections for better performance
const FeaturesSection = lazy(() => import('@/components/home/FeaturesSection'));
const HowItWorksSection = lazy(() => import('@/components/home/HowItWorksSection'));
const QuoteBanner = lazy(() => import('@/components/home/QuoteBanner'));
const TechnologySection = lazy(() => import('@/components/home/TechnologySection'));
const LearningCatalogSection = lazy(() => import('@/components/home/LearningCatalogSection'));
const LearningMethodologySection = lazy(() => import('@/components/home/LearningMethodologySection'));
const AIFeaturesSection = lazy(() => import('@/components/home/AIFeaturesSection'));
const InterviewPrepSection = lazy(() => import('@/components/home/InterviewPrepSection'));

// Section skeleton for lazy-loaded components
const SectionSkeleton = () => (
  <div className="container mx-auto px-4 py-16">
    <div className="space-y-4">
      <Skeleton className="h-10 w-64 mx-auto" />
      <Skeleton className="h-6 w-96 mx-auto" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
        {[1, 2, 3].map(i => (
          <Skeleton key={i} className="h-48 w-full" />
        ))}
      </div>
    </div>
  </div>
);

const Index = () => {
  const navigate = useNavigate();
  const { session, user, profile, userRole, isLoading, refreshUserData } = useSupabaseAuth();
  const [showDebug, setShowDebug] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loadingTimeout, setLoadingTimeout] = useState(false);

  console.log("Index page - Enhanced Auth state:", { 
    hasSession: !!session, 
    hasUser: !!user, 
    hasProfile: !!profile,
    userRole,
    isLoading,
    profileApproved: profile?.is_approved 
  });

  const handleRefreshData = async () => {
    setIsRefreshing(true);
    try {
      console.log('Manual refresh triggered by user');
      await refreshUserData();
    } catch (error) {
      console.error('Error during manual refresh:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleClearSession = async () => {
    try {
      console.log('🔑 Clearing session and local storage...');
      await supabase.auth.signOut();
      localStorage.clear();
      sessionStorage.clear();
      window.location.reload();
    } catch (error) {
      console.error('Error clearing session:', error);
    }
  };

  // Timeout handler for loading state - 8 seconds
  useEffect(() => {
    if (isLoading) {
      const timer = setTimeout(() => {
        console.warn('⏱️ Loading timeout reached (8s) - showing recovery options');
        setLoadingTimeout(true);
      }, 8000);
      
      return () => clearTimeout(timer);
    } else {
      setLoadingTimeout(false);
    }
  }, [isLoading]);

  // Show loading state while checking authentication
  if (isLoading) {
    if (loadingTimeout) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-md w-full space-y-6 text-center">
            <div className="space-y-3">
              <div className="text-5xl">⏱️</div>
              <h2 className="text-2xl font-bold">Taking Longer Than Expected</h2>
              <p className="text-muted-foreground">
                Authentication check is taking longer than usual. This may be due to a slow connection.
              </p>
            </div>
            
            <div className="space-y-3">
              <Button 
                onClick={handleRefreshData}
                disabled={isRefreshing}
                variant="default"
                className="w-full"
                size="lg"
              >
                {isRefreshing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Retrying...
                  </>
                ) : (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Retry Now
                  </>
                )}
              </Button>
              <Button 
                onClick={handleClearSession}
                variant="outline"
                className="w-full"
              >
                Clear Session & Start Fresh
              </Button>
              <Button 
                onClick={() => window.location.href = '/login'}
                variant="ghost"
                className="w-full"
              >
                Go to Login
              </Button>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-background via-background to-muted/20">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
          <div>
            <h2 className="text-xl font-semibold">Checking Authentication</h2>
            <p className="text-sm text-muted-foreground mt-2">This should only take a moment...</p>
          </div>
          <div className="mt-8">
            <Skeleton className="h-32 w-96 mx-auto" />
          </div>
        </div>
      </div>
    );
  }

  // If user is not authenticated, show the public home page
  if (!session || !user) {
    return (
      <main className="min-h-screen bg-background">
        {/* Hero loads immediately */}
        <HeroSection />
        
        {/* Below-the-fold sections load progressively */}
        <Suspense fallback={<SectionSkeleton />}>
          <LearningCatalogSection />
        </Suspense>
        
        <Suspense fallback={<SectionSkeleton />}>
          <AIFeaturesSection />
        </Suspense>
        
        <Suspense fallback={<SectionSkeleton />}>
          <InterviewPrepSection />
        </Suspense>
        
        <Suspense fallback={<SectionSkeleton />}>
          <LearningMethodologySection />
        </Suspense>
        
        <Suspense fallback={<SectionSkeleton />}>
          <FeaturesSection />
        </Suspense>
        
        <Suspense fallback={<SectionSkeleton />}>
          <HowItWorksSection />
        </Suspense>
        
        <Suspense fallback={<SectionSkeleton />}>
          <QuoteBanner />
        </Suspense>
        
        <Suspense fallback={<SectionSkeleton />}>
          <TechnologySection />
        </Suspense>
        
        <FooterSection />
      </main>
    );
  }

  // If user is authenticated but not approved and not admin/staff, show approval screen
  if (profile && !profile.is_approved && userRole !== 'admin' && userRole !== 'staff') {
    return (
      <main className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center justify-center mb-4">
              <Clock className="h-12 w-12 text-yellow-600" />
            </div>
            
            <h2 className="text-xl font-semibold text-yellow-800 mb-2">Account Pending Approval</h2>
            <p className="text-yellow-700 mb-4">
              Your account is waiting for administrator approval. You'll receive access once an admin approves your registration.
            </p>
            <p className="text-sm text-yellow-600 mb-6">
              If you believe this is an error or if you've been approved recently, please try refreshing your status below.
            </p>
            
            <div className="space-y-4 mb-6">
              <Button 
                onClick={handleRefreshData}
                disabled={isRefreshing}
                className="w-full bg-yellow-600 hover:bg-yellow-700 text-white"
                size="lg"
              >
                {isRefreshing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Checking Status...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh Approval Status
                  </>
                )}
              </Button>
            </div>
            
            <div className="bg-yellow-100 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-2 gap-4 text-xs text-yellow-700">
                <div>
                  <span className="font-medium">Email:</span>
                  <div className="truncate">{user.email}</div>
                </div>
                <div>
                  <span className="font-medium">Status:</span>
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Pending
                  </div>
                </div>
                <div>
                  <span className="font-medium">Role:</span>
                  <div>{userRole || 'Student'}</div>
                </div>
                <div>
                  <span className="font-medium">Profile:</span>
                  <div className="flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3 text-green-600" />
                    Loaded
                  </div>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <button 
                onClick={() => setShowDebug(!showDebug)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-yellow-300 rounded-md text-yellow-800 hover:bg-yellow-100 transition-colors text-sm"
              >
                <Bug className="h-4 w-4" />
                {showDebug ? 'Hide' : 'Show'} Technical Details
              </button>
              
              {showDebug && (
                <div className="mt-4 text-left space-y-4">
                  <AuthStatus />
                  <DataHealthCheck />
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    );
  }

  // For approved users, redirect directly to dashboard
  useEffect(() => {
    if (session && user && profile?.is_approved) {
      navigate("/dashboard", { replace: true });
    }
  }, [session, user, profile, navigate]);

  // This should not render for approved users due to the redirect above
  return null;
};

export default Index;
